import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';


const DealershipTable = ({ dealerships, onEdit, onDelete, onToggleStatus, onPreview, onViewLandingPage, onGenerateToken }) => {
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedDealerships = [...dealerships]?.sort((a, b) => {
    const aValue = a?.[sortField]?.toString()?.toLowerCase() || '';
    const bValue = b?.[sortField]?.toString()?.toLowerCase() || '';
    
    if (sortDirection === 'asc') {
      return aValue?.localeCompare(bValue);
    }
    return bValue?.localeCompare(aValue);
  });

  const getSortIcon = (field) => {
    if (sortField !== field) return 'ChevronsUpDown';
    return sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown';
  };

  return (
    <div className="hidden lg:block overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-muted/50 border-b border-border">
            <th className="text-left p-4">
              <button
                onClick={() => handleSort('name')}
                className="flex items-center gap-2 font-heading font-semibold text-sm text-foreground hover:text-primary transition-smooth"
              >
                Partner
                <Icon name={getSortIcon('name')} size={16} />
              </button>
            </th>
            <th className="text-left p-4">
              <button
                onClick={() => handleSort('city')}
                className="flex items-center gap-2 font-heading font-semibold text-sm text-foreground hover:text-primary transition-smooth"
              >
                Standort
                <Icon name={getSortIcon('city')} size={16} />
              </button>
            </th>
            <th className="text-left p-4">
              <span className="font-heading font-semibold text-sm text-foreground">Status</span>
            </th>
            <th className="text-left p-4">
              <span className="font-heading font-semibold text-sm text-foreground">Website</span>
            </th>
            <th className="text-left p-4">
              <span className="font-heading font-semibold text-sm text-foreground">Logo</span>
            </th>
            <th className="text-left p-4">
              <span className="font-heading font-semibold text-sm text-foreground">Landing Page URL</span>
            </th>
            <th className="text-right p-4">
              <span className="font-heading font-semibold text-sm text-foreground">Aktionen</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedDealerships?.map((dealership) => {
            const landingPageUrl = dealership?.urlSlug 
              ? `${window.location?.origin}/${dealership?.urlSlug}`
              : null;

            return (
              <tr key={dealership?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                <td className="p-4">
                  <span className="font-medium text-foreground">{dealership?.name}</span>
                </td>
                <td className="p-4">
                  <span className="text-sm text-muted-foreground">
                    {dealership?.city ? `${dealership?.postalCode} ${dealership?.city}` : '-'}
                  </span>
                </td>
                <td className="p-4">
                  <button
                    onClick={() => onToggleStatus?.(dealership)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-smooth hover:opacity-80 ${
                      dealership?.status === 'active' ?'bg-green-100 text-green-800 hover:bg-green-200' :'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                    title={`Klicken um ${dealership?.status === 'active' ? 'zu deaktivieren' : 'zu aktivieren'}`}
                  >
                    {dealership?.status === 'active' ? '✓ Aktiv' : '✕ Inaktiv'}
                  </button>
                </td>
                <td className="p-4">
                  {dealership?.website ? (
                    <a
                      href={dealership?.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                    >
                      {dealership?.website?.replace('https://', '')?.replace('http://', '')}
                      <Icon name="ExternalLink" size={14} />
                    </a>
                  ) : (
                    <span className="text-sm text-muted-foreground">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    {dealership?.logo && (
                      <div 
                        className="w-40 h-40 rounded-lg overflow-hidden flex items-center justify-center border-2 border-border shadow-md p-2"
                        style={{ backgroundColor: dealership?.logoBackgroundColor || '#FFFFFF' }}
                      >
                        <Image
                          src={dealership?.logo}
                          alt={dealership?.logoAlt || `Logo von ${dealership?.name}`}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    )}
                    {!dealership?.logo && (
                      <Icon name="Building2" size={40} color="var(--color-muted-foreground)" />
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex flex-col gap-2">
                    {landingPageUrl ? (
                      <>
                        <a
                          href={landingPageUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline font-mono inline-flex items-center gap-1 break-all"
                        >
                          {landingPageUrl}
                          <Icon name="ExternalLink" size={12} />
                        </a>
                        <button
                          onClick={() => onGenerateToken?.(dealership)}
                          className="text-xs text-primary hover:underline text-left inline-flex items-center gap-1"
                        >
                          <Icon name="Key" size={12} />
                          Verifizierungs-Link generieren
                        </button>
                      </>
                    ) : (
                      <span className="text-sm text-muted-foreground italic">URL wird generiert...</span>
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onPreview(dealership)}
                      className="p-2 rounded-lg hover:bg-muted transition-smooth"
                      title="Vorschau"
                    >
                      <Icon name="Eye" size={18} color="var(--color-muted-foreground)" />
                    </button>
                    <button
                      onClick={() => onViewLandingPage(dealership)}
                      className="p-2 rounded-lg hover:bg-muted transition-smooth"
                      title="Landing Page öffnen"
                    >
                      <Icon name="ExternalLink" size={18} color="var(--color-primary)" />
                    </button>
                    <button
                      onClick={() => onEdit(dealership)}
                      className="p-2 rounded-lg hover:bg-muted transition-smooth"
                      title="Bearbeiten"
                    >
                      <Icon name="Edit2" size={18} color="var(--color-primary)" />
                    </button>
                    <button
                      onClick={() => onDelete(dealership)}
                      className="p-2 rounded-lg hover:bg-error/10 transition-smooth"
                      title="Löschen"
                    >
                      <Icon name="Trash2" size={18} color="var(--color-error)" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DealershipTable;